import { instance } from 'api/api';
import { APIResponse } from 'tools/types';
import { FormComponentValue } from 'components/common/form/layout/control';
import { ISaveReportContent } from 'form-templates/ReportForm';
import {
    DatasourceColumnsDataResponse,
    ReportFilterSegmentValuesData,
} from 'components/report-content/hooks/useReportContentSettings';
import {
    ComponentSettings,
    ComponentsType,
    DisplayMask,
    EditorData,
    IComponentProperties,
    IKpiChartOverlayDataRow,
    IReportState,
    ManageFiltersInstance,
    ManageFiltersRelevantValuesResponse,
} from '@report-content';

/** The applied picker keys an AI Insight block posts with its generation, as the preview posts them. */
export interface AiInsightCanvasScope {
    /** '' = a cleared picker. */
    primaryResource: string;
    resources: string[];
}

interface AiInsightResourceChip {
    key: string;
    label: string;
    resource_type: string;
    resource_id: number;
    type_label: string;
    short_label: string;
    logo_key: string;
    icon: { type: string; value: string };
    // Published to Concierge as end users get it; absent from a backend that predates the verdict.
    published?: boolean;
    // The resource behind a stored row no longer exists.
    deleted: boolean;
}

export interface ReportPreviewComponent {
    internalName: ComponentsType | null;
    settings: ComponentSettings | null;
    displayMasks: Array<DisplayMask>;
}

interface AiInsightResourceDefaultPrimary {
    key: string;
    label: string;
    logo_key: string;
    reason: string;
}

interface AiInsightResourceCandidatesResponse {
    own_dataset: { dataset_id: number; label: string; in_kg: boolean } | null;
    values: Array<AiInsightResourceChip>;
    chosen_primary: string | null;
    primary_candidates: Array<AiInsightResourceChip>;
    default_primary: AiInsightResourceDefaultPrimary | null;
    empty_primary_notice: string;
    // Set while the stored primary points at a resource that no longer exists.
    deleted_primary_notice: string | null;
    pool_empty?: boolean;
}

// MI-30274: one canonical knowledge-graph resource picker — datasets, source models and MCP
// domains, single control shape for Primary (single-select) and Additional (multi-select).
export interface AiInsightResourceCandidates {
    chips: Array<FormComponentValue>;
    ownDataset: { datasetId: number; label: string; inKg: boolean } | null;
    chosenPrimary: string | null;
    primaryCandidates: Array<FormComponentValue>;
    defaultPrimary: AiInsightResourceDefaultPrimary | null;
    emptyPrimaryNotice: string;
    deletedPrimaryNotice: string | null;
    poolEmpty?: boolean;
}

const toFormComponentValue = (chip: AiInsightResourceChip): FormComponentValue => ({
    value: chip.key,
    label: chip.label,
    props: {
        icon: chip.icon,
        resource_type: chip.resource_type,
        type_label: chip.type_label,
        short_label: chip.short_label,
        logo_key: chip.logo_key,
        ...(undefined !== chip.published ? { published: chip.published } : {}),
        deleted: chip.deleted,
    },
});

export const reportAPI = {
    async getAvailableKpiChartOverlays(elementId: number): Promise<Array<FormComponentValue>> {
        const response = await instance.get<{ values: Array<FormComponentValue> }>(
            `data/report/${elementId}/kpi-chart/overlay-metrics/available`,
        );
        return response.data.values;
    },

    async setKpiChartSummaryState(elementId: number, blockUid: string, isOpen: boolean): Promise<void> {
        await instance.post(`data/report/${elementId}/kpi-chart/${blockUid}/summary-state`, {
            state: isOpen ? 'Y' : 'N',
        });
    },

    async getKpiChartSummaryState(elementId: number, blockUid: string): Promise<boolean> {
        const response = await instance.get<{ state: boolean }>(
            `data/report/${elementId}/kpi-chart/${blockUid}/summary-state`,
        );
        return response.data.state;
    },

    async getKpiChartOverlaysData<T extends IComponentProperties>(
        elementId: number,
        overlayIds: Array<string>,
        contentSettings: T,
        filters: string | null,
    ): Promise<{ [overlayId: string]: Array<IKpiChartOverlayDataRow> }> {
        const settingsToPost = { ...contentSettings, instances: undefined };
        const response = await instance.post<{ overlays: { [overlayId: string]: Array<IKpiChartOverlayDataRow> } }>(
            `data/report/${elementId}/kpi-chart/overlays-data`,
            {
                overlayIds: overlayIds,
                properties: JSON.stringify(settingsToPost),
                filters: filters,
            },
        );
        return response.data.overlays;
    },

    async getReportState(elementId: number, segmentValueId: number, stateId?: number): Promise<IReportState> {
        const response = await instance.get<APIResponse<IReportState>>(
            `data/report/element/${elementId}/${segmentValueId}/state${stateId ? `/${stateId}` : ''}`,
        );
        return response.data.data;
    },

    async saveReportState(elementId: number, segmentValueId: number, state: IReportState['state']): Promise<number> {
        const response = await instance.post<APIResponse<{ id: number }>>(
            `data/report/element/${elementId}/${segmentValueId}/state`,
            { state },
        );
        return response.data.data.id;
    },

    async saveReportFilters(
        elementId: number,
        segmentValueId: number,
        filters: IReportState['filters'],
    ): Promise<number> {
        const response = await instance.post<APIResponse<{ id: number }>>(
            `data/report/element/${elementId}/${segmentValueId}/filters`,
            { filters },
        );
        return response.data.data.id;
    },

    async getFiltersRelevantValues(
        elementId: number,
        segmentValueId: number,
        filters: string | null,
    ): Promise<ManageFiltersRelevantValuesResponse> {
        const response = await instance.post<APIResponse<ManageFiltersRelevantValuesResponse>>(
            `data/report/element/${elementId}/${segmentValueId}/filters/relevant-values`,
            { filters },
        );
        return response.data.data;
    },

    async getContentEditorData(elementId: number): Promise<EditorData> {
        const response = await instance.get<APIResponse<EditorData>>(
            `data/editor/report/${elementId}/get-content-editor-data`,
        );
        return response.data.data;
    },

    async getComponentData<T extends IComponentProperties>(
        settings: ComponentSettings,
        contentSettings: T,
        filters: string | null,
        aiInsightScope?: AiInsightCanvasScope,
    ): Promise<any> {
        const settingsToPost = { ...contentSettings, instances: undefined };

        const response = await instance.post<APIResponse<any>>(
            `data/report/component-data/${contentSettings.elementId}/${contentSettings.segmentValueId}`,
            {
                properties: JSON.stringify(settingsToPost),
                settings: JSON.stringify(settings),
                filters: filters,
                ...(aiInsightScope
                    ? { primary_resource: aiInsightScope.primaryResource, resources: aiInsightScope.resources }
                    : {}),
            },
        );
        return response.data.data;
    },

    async getPreviewComponent(elementId: number): Promise<ReportPreviewComponent> {
        const response = await instance.get<APIResponse<ReportPreviewComponent>>(
            `data/report/${elementId}/preview-component`,
        );
        return response.data.data;
    },

    async getInstances(
        elementId: number,
        segmentValueId: number,
    ): Promise<{ instances: Array<ManageFiltersInstance>; hasInstances: boolean }> {
        if (!elementId) {
            return {
                instances: [],
                hasInstances: false,
            };
        }

        const response = await instance.get<APIResponse<any>>(`data/report/renderer-instances/${elementId}`, {
            params: {
                segment: segmentValueId,
            },
        });
        return {
            instances: response.data.data?.instances ?? [],
            hasInstances: response.data.data?.hasInstances ?? false,
        };
    },

    async getSegmentValuesList(
        elementId: number,
        segmentValueId: number,
        measurement_time?: string,
    ): Promise<ReportFilterSegmentValuesData> {
        let config = {};
        if (measurement_time) {
            config = {
                params: {
                    measurement_time: measurement_time,
                },
            };
        }
        const response = await instance.get<APIResponse<ReportFilterSegmentValuesData>>(
            `data/report/renderer-segments/${elementId}`,
            config,
        );
        return response.data.data;
    },
    async getColumnsSource(dataset: string, filter: string): Promise<boolean> {
        const response = await instance.get<APIResponse<DatasourceColumnsDataResponse>>(
            `data/dataset/${dataset}/get-columns-for-source/filter/${filter}`,
        );
        return response.data.data.has_access_map;
    },
    async getReportVisualizationList(elementId: number): Promise<Array<FormComponentValue>> {
        const response = await instance.get<APIResponse<Array<FormComponentValue>>>(
            `data/editor/report/${elementId}/get-external-visualizations-list`,
        );
        return response.data.data;
    },
    async getAiInsightResourceCandidates(
        elementId: number,
        reportAiInsightId: number = 0,
    ): Promise<AiInsightResourceCandidates> {
        const response = await instance.get<APIResponse<AiInsightResourceCandidatesResponse>>(
            `data/editor/report/${elementId}/ai-insight/resource-candidates`,
            { params: reportAiInsightId > 0 ? { report_ai_insight_id: reportAiInsightId } : {} },
        );
        const {
            own_dataset,
            values,
            chosen_primary,
            primary_candidates,
            default_primary,
            empty_primary_notice,
            deleted_primary_notice,
            pool_empty,
        } = response.data.data;

        return {
            chips: values.map(toFormComponentValue),
            ownDataset: own_dataset
                ? { datasetId: own_dataset.dataset_id, label: own_dataset.label, inKg: own_dataset.in_kg }
                : null,
            chosenPrimary: chosen_primary,
            primaryCandidates: primary_candidates.map(toFormComponentValue),
            defaultPrimary: default_primary,
            emptyPrimaryNotice: empty_primary_notice,
            deletedPrimaryNotice: deleted_primary_notice,
            poolEmpty: pool_empty,
        };
    },
    async saveReportContent(elementId: number, data: ISaveReportContent): Promise<any> {
        const response = await instance.post<APIResponse<Array<any>>>(
            `data/editor/report/save-renderer-blocks/element/${elementId}`,
            data,
        );
        return response.data;
    },
    async getStoplightMethods(): Promise<Array<FormComponentValue>> {
        const response = await instance.get<APIResponse<Array<FormComponentValue>>>(
            `data/editor/report/get-stoplight-calc-methods`,
        );
        return response.data.data;
    },
};
